import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Mail, Users, Check, ShieldCheck, Loader2, AlertCircle, Save, Crown,
  Plus, Search, X, ArrowDownUp, FileDown,
} from "lucide-react";
import SEO from "../components/SEO";
import { NewStructureUserForm } from "./MssanteCreerMessagerie";
import { getIdTypeMeta, getUserBadge } from "../lib/userIdTypes";
import { API } from "../apiBase";
export default function MssanteAcces() {
  const { id: orderId } = useParams();
  const [mailbox, setMailbox] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [coIds, setCoIds] = useState([]);       // cotitulaires
  const [delIds, setDelIds] = useState([]);     // délégataires
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [mb, ac] = await Promise.all([
        axios.get(`${API}/pro/mailboxes/${orderId}`),
        axios.get(`${API}/pro/mailboxes/${orderId}/access`),
      ]);
      setMailbox(mb.data);
      setAccess(ac.data);
      setCoIds(ac.data?.cotitulaire_user_ids || []);
      setDelIds(ac.data?.delegataire_user_ids || []);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "Messagerie introuvable." : "Impossible de charger la messagerie.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const usersById = useMemo(() => {
    const m = {};
    (access?.available_users || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [access]);
  const responsible = usersById[access?.responsible_user_id];

  // Available pool excludes responsible (always permanent)
  const eligible = useMemo(() =>
    (access?.available_users || []).filter((u) => u.id !== access?.responsible_user_id)
  , [access]);

  const addCotitulaire = (uid) => {
    setSuccess(false);
    setCoIds((s) => s.includes(uid) ? s : [...s, uid]);
    setDelIds((s) => s.filter((x) => x !== uid));
  };
  const removeCotitulaire = (uid) => {
    setSuccess(false);
    setCoIds((s) => s.filter((x) => x !== uid));
  };
  const addDelegataire = (uid) => {
    setSuccess(false);
    setDelIds((s) => s.includes(uid) ? s : [...s, uid]);
    setCoIds((s) => s.filter((x) => x !== uid));
  };
  const removeDelegataire = (uid) => {
    setSuccess(false);
    setDelIds((s) => s.filter((x) => x !== uid));
  };
  const moveCoToDel = (uid) => addDelegataire(uid);
  const moveDelToCo = (uid) => addCotitulaire(uid);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await axios.put(`${API}/pro/mailboxes/${orderId}/access`, {
        cotitulaire_user_ids: coIds,
        delegataire_user_ids: delIds,
      });
      setCoIds(data.cotitulaire_user_ids || []);
      setDelIds(data.delegataire_user_ids || []);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2800);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Sauvegarde impossible.");
    } finally {
      setSaving(false);
    }
  };

  const onUserCreated = async () => {
    await load();
    setAddOpen(false);
  };

  const downloadReceipt = async () => {
    try {
      const res = await axios.get(`${API}/pro/mailboxes/${orderId}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recapitulatif-mssante-${mailbox?.mailbox_name || orderId}.pdf`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
    } catch (e) {
      setErr("Téléchargement du récapitulatif impossible.");
    }
  };

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-acces">
      <SEO title="Gestion des accès · MetaConnect" canonical={`/mssante/espace/messagerie/${orderId}/acces`} noIndex />

      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Link to="/mssante/espace" className="text-[12px] text-[#475569] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Retour à mon espace
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600">
            <ShieldCheck size={13} /> Gestion des accès
          </div>
          <h1 className="mt-3 font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Qui peut utiliser cette messagerie ?
          </h1>
          {mailbox && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <div className="inline-flex items-center gap-2 text-[13px] rounded-full px-4 py-2 bg-[rgba(110,231,197,0.08)] border border-[rgba(110,231,197,0.25)] text-emerald-600">
                <Mail size={13} /> {mailbox.mailbox_full_email}
              </div>
              <button onClick={downloadReceipt} type="button"
                      data-testid="acces-receipt-download"
                      className="inline-flex items-center gap-2 text-[12.5px] rounded-full px-3.5 py-1.5 bg-[#EFF6FF] border border-[rgba(127,179,255,0.32)] text-[#0F2B5B] hover:bg-[rgba(45,123,255,0.22)] transition">
                <FileDown size={12} /> Télécharger le récapitulatif (PDF)
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="hex-soft py-8 lg:py-12">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid gap-5">
          {loading && <div className="text-[#475569] text-[13px]">Chargement…</div>}
          {err && !access && <div className="text-[#FFB4B4] text-[13px]">{err}</div>}

          {!loading && access && (
            <>
              {/* Responsible */}
              <ResponsibleCard responsible={responsible} />

              {/* Add user inline */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[12.5px] text-[#475569] max-w-2xl">
                  Définissez les <strong className="text-[#0F2B5B]">cotitulaires</strong> (titulaires secondaires)
                  et les <strong className="text-[#0F2B5B]">délégataires</strong> (accès en lecture). La modification
                  est appliquée immédiatement côté MetaConnect ; la synchronisation Maincare est faite par notre
                  équipe sous 24-48h ouvrées.
                </p>
                {!addOpen && (
                  <button onClick={() => setAddOpen(true)} data-testid="acces-add-user"
                          className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5">
                    <Plus size={13} /> Nouvel utilisateur dans la structure
                  </button>
                )}
              </div>
              {addOpen && (
                <NewStructureUserForm structureId={access.structure_id}
                                      onCancel={() => setAddOpen(false)}
                                      onCreated={onUserCreated} />
              )}

              {/* Cotitulaires & Délégataires */}
              <RoleCard
                kind="cotitulaire"
                title="Cotitulaires"
                hint="Titulaires secondaires de la boîte — pleine utilisation."
                selectedIds={coIds}
                eligible={eligible}
                otherIds={delIds}
                usersById={usersById}
                onAdd={addCotitulaire}
                onRemove={removeCotitulaire}
                onMove={moveCoToDel}
                moveToLabel="Passer en délégataire"
              />
              <RoleCard
                kind="delegataire"
                title="Délégataires"
                hint="Accès en lecture / délégation, sans le statut de titulaire."
                selectedIds={delIds}
                eligible={eligible}
                otherIds={coIds}
                usersById={usersById}
                onAdd={addDelegataire}
                onRemove={removeDelegataire}
                onMove={moveDelToCo}
                moveToLabel="Passer en cotitulaire"
              />

              {err && (
                <div className="flex items-start gap-2 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.08)] px-4 py-3 text-[13px] text-[#DC2626]">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{err}</span>
                </div>
              )}

              {/* Save bar */}
              <div className="mc-card p-4 lg:p-5 flex items-center justify-between gap-3 flex-wrap sticky bottom-4 z-10
                              bg-white/95 backdrop-blur-sm">
                <div className="text-[12.5px] text-[#475569]">
                  <span className="text-[#0F2B5B]">{coIds.length}</span> cotitulaire(s) · <span className="text-[#0F2B5B]">{delIds.length}</span> délégataire(s)
                </div>
                <div className="flex items-center gap-3">
                  {success && (
                    <span data-testid="acces-success" className="text-[12.5px] text-emerald-600 inline-flex items-center gap-1.5">
                      <Check size={14} /> Les accès de la boîte ont bien été mis à jour.
                    </span>
                  )}
                  <button onClick={save} disabled={saving} data-testid="acces-save"
                          className="btn-primary px-6 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Enregistrement..." : "Enregistrer les accès"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

// =================== sub-components ===================

function ResponsibleCard({ responsible }) {
  if (!responsible) {
    return (
      <div className="mc-card p-5 text-[12.5px] text-[#475569]">Aucun responsable défini sur cette messagerie.</div>
    );
  }
  return (
    <div className="mc-card p-5 lg:p-6" data-testid="acces-responsible">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1] mb-3 inline-flex items-center gap-2">
        <Crown size={12} /> Responsable principal
      </div>
      <div className="rounded-lg border border-emerald-200 bg-[rgba(110,231,197,0.06)] p-4 flex items-center gap-3 flex-wrap">
        <div className="grid place-items-center h-10 w-10 rounded-full bg-[rgba(110,231,197,0.18)] text-emerald-600 shrink-0">
          <Crown size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[#0F2B5B] font-medium">{responsible.first_name} {responsible.last_name}</div>
          <div className="text-[12px] text-[#475569]">
            {responsible.profession}
            {responsible.id_type && responsible.professional_id && <> · {getIdTypeMeta(responsible.id_type)?.label || responsible.id_type} <span className="font-mono">{responsible.professional_id}</span></>}
            <span className="mx-2">·</span>{responsible.email}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[rgba(110,231,197,0.4)] text-emerald-600">
          Permanent
        </span>
      </div>
    </div>
  );
}

function RoleCard({ kind, title, hint, selectedIds, eligible, otherIds, usersById, onAdd, onRemove, onMove, moveToLabel }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const otherSet = useMemo(() => new Set(otherIds), [otherIds]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return eligible
      .filter((u) => !selectedSet.has(u.id))
      .filter((u) => !q
        || `${u.first_name} ${u.last_name}`.toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.professional_id || "").toLowerCase().includes(q));
  }, [eligible, selectedSet, query]);

  const accent = kind === "cotitulaire"
    ? { ring: "ring-[var(--mc-blue-400)]", chip: "border-[rgba(127,179,255,0.32)] text-[#0F2B5B] bg-[#EFF6FF]" }
    : { ring: "ring-[#a88cff]", chip: "border-[rgba(168,140,255,0.4)] text-[#d2c2ff] bg-[rgba(168,140,255,0.06)]" };

  return (
    <div className="mc-card p-5 lg:p-6" data-testid={`acces-${kind}-card`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1] inline-flex items-center gap-2">
            <Users size={12} /> {title}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(45,123,255,0.16)] text-[#0F2B5B] normal-case tracking-normal"
                  data-testid={`acces-${kind}-count`}>
              {selectedIds.length} sélectionné(s)
            </span>
          </div>
          <p className="mt-1 text-[12px] text-[#475569] max-w-xl">{hint}</p>
        </div>
      </div>

      {/* Selected badges */}
      {selectedIds.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-[12.5px] text-[#475569] mb-4"
             data-testid={`acces-${kind}-empty`}>
          Aucun {title.toLowerCase().slice(0, -1)} pour le moment.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mb-4" data-testid={`acces-${kind}-badges`}>
          {selectedIds.map((uid) => {
            const u = usersById[uid];
            if (!u) return null;
            return (
              <span key={uid}
                    className={`inline-flex items-center gap-2 rounded-full border pl-2.5 pr-1 py-1 ${accent.chip}`}
                    data-testid={`acces-${kind}-badge-${uid}`}>
                <span className="text-[12px]">{u.first_name} {u.last_name}</span>
                {onMove && (
                  <button onClick={() => onMove(uid)} title={moveToLabel}
                          className="text-[10px] text-[#475569] hover:text-[#0F2B5B] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1">
                    <ArrowDownUp size={10} />
                  </button>
                )}
                <button onClick={() => onRemove(uid)} title="Retirer"
                        data-testid={`acces-${kind}-remove-${uid}`}
                        className="text-[#475569] hover:text-[#FFB4B4] grid place-items-center h-5 w-5 rounded-full hover:bg-[rgba(255,120,120,0.10)]">
                  <X size={11} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Picker (search + dropdown) */}
      <div className="relative">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#475569]" />
        <input data-testid={`acces-${kind}-search`}
               className="mc-input w-full pl-9"
               placeholder={`Rechercher pour ajouter un ${title.toLowerCase().slice(0, -1)}…`}
               value={query}
               onFocus={() => setOpen(true)}
               onBlur={() => setTimeout(() => setOpen(false), 180)}
               onChange={(e) => { setQuery(e.target.value); setOpen(true); }} />
        {open && (
          <ul className="absolute z-20 mt-1 left-0 right-0 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-[var(--mc-navy-900)] shadow-lg">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-[12.5px] text-[#475569]">Aucun utilisateur disponible.</li>
            ) : filtered.map((u) => {
              const inOther = otherSet.has(u.id);
              return (
                <li key={u.id}>
                  <button type="button"
                          onMouseDown={(e) => { e.preventDefault(); onAdd(u.id); setQuery(""); setOpen(false); }}
                          data-testid={`acces-${kind}-pick-${u.id}`}
                          className="w-full text-left px-4 py-2.5 text-[12.5px] hover:bg-[rgba(45,123,255,0.16)] transition flex items-center gap-2">
                    <Users size={12} className="text-[#0369A1] shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="text-[#0F2B5B] truncate">{u.first_name} {u.last_name}</span>
                      <span className="text-[#475569]"> · {u.profession} · {u.email}</span>
                    </span>
                    {inOther && (
                      <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-[rgba(168,140,255,0.12)] text-[#d2c2ff] border border-[rgba(168,140,255,0.4)] shrink-0">
                        actuellement {kind === "cotitulaire" ? "délégataire" : "cotitulaire"}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
